/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use an screenfailures by id.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type the state of a request. 
 */
import { IRequestState } from '@ngt/request-utilities';

/*
 * Used to type actions.
 */

/*
 * Used to get access to dispatch to dispatch actions to the store.
 */
import { useDispatch } from 'react-redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to get access to backend types.
 */
import * as Dtos from '../../api/dtos';

/*
 * used to type actions.
 */
import { IScreenFailuresStore, screenFailuresActions, screenFailuresSelectors, useScreenFailuresSelector } from '../../store/modules/data/screenFailures';

/*
 * Used to remove the first parameter from the base action creator function type.
 */
import { OmitFirstThreeArgs } from '../../utils/OmitArgs';

/*
 * Used to type action creators with first parameter bound to a static. 
 */
import { BoundActionCreator } from '../../utils/BoundActionCreator';

/*
 * Used to bind action creators to the store but keep their assigned type property.
 */
import bindActionCreatorsWithType from '../../utils/bindActionCreatorsWithType';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

export interface IUseScreenFailuresActions {
    load: BoundActionCreator<OmitFirstThreeArgs<typeof screenFailuresActions.load>>;
    clear: BoundActionCreator<OmitFirstThreeArgs<typeof screenFailuresActions.clear>>;
}

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const useScreenFailures = (autoLoad?: boolean): [
    Dtos.ScreenFailure[] | null,
    IRequestState<Dtos.ResponseStatus>,
    IUseScreenFailuresActions
] => {
    const dispatch = useDispatch();

    const actions: IUseScreenFailuresActions = React.useMemo(() => {
        const load = () => screenFailuresActions.load();
        load.type = screenFailuresActions.load.type;

        const clear = () => screenFailuresActions.clear();
        clear.type = screenFailuresActions.clear.type;

        return bindActionCreatorsWithType({
            load,
            clear
        }, dispatch);
    }, [screenFailuresActions, dispatch]);

    const screenFailuresSelector = React.useCallback((state: IScreenFailuresStore) => {
        return screenFailuresSelectors.selectScreenFailures(state);
    }, [screenFailuresSelectors.selectScreenFailures]);

    const loadStateSelector = React.useCallback((state: IScreenFailuresStore) => {
        return screenFailuresSelectors.selectLoadState(state);
    }, [screenFailuresSelectors.selectLoadState]);

    const screenFailures = useScreenFailuresSelector(screenFailuresSelector);

    const loadState = useScreenFailuresSelector(loadStateSelector);

    React.useEffect(() => {
        if (autoLoad) {
            actions.load();

            return () => {
                actions.clear();
            };
        }

        return () => { };
    }, [autoLoad]);

    return [
        screenFailures as Dtos.ScreenFailure[] | null,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useScreenFailures;